import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { INSTALL_FLOW_STEPS, type StepContext } from '@/installFlow/installFlowConfig'
import { SensorScanStep } from '@/components/installFlow/steps/SensorScanStep'
import { IntroStep } from '@/components/installFlow/steps/IntroStep'
import { ScanStep } from '@/components/installFlow/steps/ScanStep'
import { LocationStep } from '@/components/installFlow/steps/LocationStep'
import { SublocationStep } from '@/components/installFlow/steps/SublocationStep'
import { InstallationStep } from '@/components/installFlow/steps/InstallationStep'
import { DetailsStep } from '@/components/installFlow/steps/DetailsStep'
import { LinkStep } from '@/components/installFlow/steps/LinkStep'
import { HealthStep } from '@/components/installFlow/steps/HealthStep'
import { Check } from 'lucide-react'

/**
 * Discriminated union type for install flow search params.
 * Each step variant defines its required parameters, making invalid states unrepresentable.
 *
 * installationName: Used when creating a NEW installation (entered on installation step, needed on details step)
 * installationId: Used when working with an EXISTING installation
 * scanId: Optional nameplate scan ID for auto-filling equipment details from scanned photo
 * installSensorKitSerialNumber: Sensor kit serial number captured at beginning or from sync page
 */
export type InstallFlowSearch =
  | { step: 'sensor-scan'; installSensorKitSerialNumber?: string }
  | { step: 'intro'; installSensorKitSerialNumber?: string; scanId?: string }
  | { step: 'scan'; installSensorKitSerialNumber?: string; scanId?: string }
  | { step: 'location'; installSensorKitSerialNumber?: string; scanId?: string }
  | {
      step: 'installation'
      locationId: number
      installSensorKitSerialNumber?: string
      scanId?: string
    }
  | {
      step: 'sublocation'
      locationId: number
      installationName?: string // Optional: only present when creating NEW installation
      installSensorKitSerialNumber?: string
      scanId?: string
    }
  | {
      step: 'details'
      locationId: number
      sublocationId: number
      installationName?: string // Optional: only present when creating NEW installation
      installationId?: number // Optional: only present when editing EXISTING installation
      installSensorKitSerialNumber?: string
      scanId?: string
    }
  | {
      step: 'link'
      locationId: number
      sublocationId: number
      installationId: number
      installSensorKitSerialNumber: string // Required for link step
      scanId?: string
    }
  | {
      step: 'health'
      locationId: number
      sublocationId: number
      installationId: number
      installSensorKitSerialNumber?: string
      scanId?: string
    }

/**
 * Search params schema for the new flat install flow route.
 * Uses superRefine to validate step-specific parameter requirements.
 */
const installFlowSearchSchema = z
  .object({
    // Current step in the flow
    step: z
      .enum(['sensor-scan', 'intro', 'scan', 'location', 'installation', 'sublocation', 'details', 'link', 'health'])
      .optional()
      .default('sensor-scan'),

    // Entity IDs accumulated as user progresses through flow
    // Using z.coerce.number() to handle string-to-number conversion gracefully
    locationId: z.coerce.number().int().positive().optional(),
    installationId: z.coerce.number().int().positive().optional(),
    sublocationId: z.coerce.number().int().positive().optional(),

    // Installation name (when creating NEW installation, entered on installation step)
    installationName: z.string().optional(),

    // Optional pre-filled sensor serial number (from QR code scan)
    installSensorKitSerialNumber: z.string().optional(),

    // Optional nameplate scan ID (UUID) for auto-filling equipment details
    scanId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Validate step-specific requirements
    // These validations mirror the guard logic in installFlowConfig.ts

    // Override default step based on installSensorKitSerialNumber
    // If sensor serial is present, skip sensor-scan step and go to intro
    if (data.step === 'sensor-scan' && data.installSensorKitSerialNumber) {
      // This is handled by setting the step explicitly in the URL
      // No validation error needed
    }

    if (['installation', 'sublocation', 'details', 'link', 'health'].includes(data.step) && !data.locationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'locationId is required for this step',
        path: ['locationId'],
      })
    }

    if (['details', 'link', 'health'].includes(data.step) && !data.sublocationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'sublocationId is required for this step',
        path: ['sublocationId'],
      })
    }

    if (['link', 'health'].includes(data.step) && !data.installationId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'installationId is required for this step',
        path: ['installationId'],
      })
    }

    // Link step requires sensor serial number
    if (data.step === 'link' && !data.installSensorKitSerialNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'installSensorKitSerialNumber is required for link step',
        path: ['installSensorKitSerialNumber'],
      })
    }
  }) as z.ZodType<InstallFlowSearch>

export const Route = createFileRoute(
  '/_authenticated/organization/$organizationId/install-flow'
)({
  component: InstallFlowLayout,
  validateSearch: installFlowSearchSchema,
  beforeLoad: ({ search }) => {
    // Navigation guard: Validate that the current step is accessible
    // This runs before the component loads, preventing invalid navigation
    const currentStepConfig = INSTALL_FLOW_STEPS.find((step) => step.id === search.step)

    if (!currentStepConfig) {
      // Invalid step - redirect to intro
      // Zod should prevent this, but handle it defensively
      throw new Error('Invalid step')
    }

    // Note: We don't check isAccessible here because:
    // 1. The Zod superRefine validation already ensures required params exist
    // 2. Draft state checks are handled by individual step components
    // 3. If someone deep links with valid params, they should be allowed in

    return {
      customNav: () => <FlowProgressIndicatorNav />,
      removeContentPadding: true,
      hideFeedbackFooter: true
    }
  },
})

/**
 * Step mapping object that associates step IDs with their components.
 */
const STEP_COMPONENTS = {
  'sensor-scan': SensorScanStep,
  intro: IntroStep,
  scan: ScanStep,
  location: LocationStep,
  sublocation: SublocationStep,
  installation: InstallationStep,
  details: DetailsStep,
  link: LinkStep,
  health: HealthStep,
} as const

/**
 * Progress indicator component for custom nav.
 * Uses URL params as source of truth (survives refresh).
 */
function FlowProgressIndicatorNav() {
  const searchParams = Route.useSearch()

  // Create context object from URL params for step evaluation
  // Use optional access since not all step variants have all properties
  const context: StepContext = {
    currentStep: searchParams.step as any,
    locationId: 'locationId' in searchParams ? searchParams.locationId : undefined,
    sublocationId: 'sublocationId' in searchParams ? searchParams.sublocationId : undefined,
    installationId: 'installationId' in searchParams ? searchParams.installationId : undefined,
    installSensorKitSerialNumber: searchParams.installSensorKitSerialNumber,
  }

  // Filter steps to only show those visible in nav
  const visibleSteps = INSTALL_FLOW_STEPS.filter((step) => step.showInNav !== false)

  // Find current step index in visible steps
  const currentVisibleStepIndex = visibleSteps.findIndex((step) => step.id === searchParams.step)
  // If current step is hidden (sensor-scan, intro), use -1 so no nav item is highlighted
  const activeStepIndex = currentVisibleStepIndex

  return (
    <div className="w-full py-4 px-4">
      <div className="relative px-4">
        {/* Horizontal line connecting all steps */}
        <div className="absolute left-4 right-4 h-0.5 bg-muted" style={{ top: '16px' }} />

        {/* Step indicators */}
        <div className="relative grid gap-4" style={{ gridTemplateColumns: `repeat(${visibleSteps.length}, 1fr)` }}>
          {visibleSteps.map((step, index) => {
            const isCompleted = step.isComplete(context)
            const label = step.label

            return (
              <div key={step.id} className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full border-white border-4 flex items-center justify-center text-sm font-medium ${
                    index <= activeStepIndex
                      ? 'bg-thalo-blue text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                <div className="mt-2 text-xs text-center">{label}</div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Layout component for the new flat install flow.
 * Uses URL params as source of truth, TanStack Query for data fetching.
 * Navigation guards are handled by Zod validation in the route config.
 */
function InstallFlowLayout() {
  const searchParams = Route.useSearch()

  // Get the appropriate component for the current step
  const StepComponent = STEP_COMPONENTS[searchParams.step]

  return (
    <div className="">
      {/* Step router - renders the appropriate step component */}
      {StepComponent && <StepComponent />}
    </div>
  )
}
